import React, { useEffect, useReducer, useRef, useState } from "react";
import { Box, Alert, TextField, Button, Paper, Tooltip, InputBase, InputAdornment, MenuItem } from "@mui/material";
import { getSession } from "../../../auth.js";
import api from "../../../api";
import Loading from "../../Loading/index.js";
import { lockedReducer } from '../../../utils/defaultReducer.js'
import './styles.css'
import ChatWindow from "./ChatWindow/index.js";
import AccountList from "./AccountList/index.js";
import ScoutSocketComponent, { treatEvents } from "./SocketComponent/index.js";
import SearchIcon from "@mui/icons-material/Search";
import TextSearch from "./TextSearch/index.js";
import CRMSideBar from "./CRMSideBar/index.js";
import AnalyticsSideBar from "./AnalyticsSidebar/index.js";
import HistoryMessagesDialog from "../../Dialogs/HistoryMessagesDialog";
import { useLocation , useHistory } from 'react-router-dom';

import ContentUnavailable from "../../ContentUnavailable"
import { useMenu } from "../../../contexts/MenuContext"
import ScoutLogo from "../../../assets/images/kwik-scout-logo.png"

const scoutAccountFilterOptions = [
  { value: 'name', label: 'Nome' },
  { value: 'phone_number', label: 'Número' },
]

const scoutAccountsPageSize = 10

const normalizeAccountsResponse = (data, page) => {
  const results = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : [])

  return {
    results,
    count: Number(data?.count ?? results.length),
    hasMore: Boolean(data?.next),
    page,
  }
}

const mergeAccountsById = (currentAccounts = [], nextAccounts = [], reset = false) => {
  const mergedAccounts = reset ? [] : [...currentAccounts]
  const accountIndexes = new Map(mergedAccounts.map((account, index) => [String(account.id), index]))

  nextAccounts.forEach((account) => {
    const accountId = String(account?.id || '')
    if (!accountId) return

    if (accountIndexes.has(accountId)) {
      const accountIndex = accountIndexes.get(accountId)
      mergedAccounts[accountIndex] = { ...mergedAccounts[accountIndex], ...account }
      return
    }

    accountIndexes.set(accountId, mergedAccounts.length)
    mergedAccounts.push(account)
  })

  return mergedAccounts
}

const normalizeAccountFilterText = (value = '') => (
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
)

const filterAccountsLocally = (accounts = [], filterBy = 'name', filter = '') => {
  const filterValue = String(filter || '').trim()
  if (!filterValue) return accounts

  if (filterBy === 'phone_number') {
    const phoneFilter = filterValue.replace(/\D/g, '')
    if (!phoneFilter) return []
    return accounts.filter((account) => String(account?.phone_number || '').replace(/\D/g, '').includes(phoneFilter))
  }

  const nameFilter = normalizeAccountFilterText(filterValue)
  return accounts.filter((account) => normalizeAccountFilterText(account?.name).includes(nameFilter))
}

const getAccounts = async (enterprise_id, setState, {
  page = 1,
  filterBy = 'name',
  filter = '',
  reset = true,
  loadingKey = 'loading',
  currentAccounts = [],
  shouldApply = () => true,
} = {}) => {

  setState({ [loadingKey]: true })
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(scoutAccountsPageSize),
    filter_by: filterBy || 'name',
  })
  if (String(filter || '').trim()) {
    params.set('filter', String(filter).trim())
  }
  const res = await api.get(
    `api/enterprises/${enterprise_id}/scout_accounts/?${params.toString()}`, {timeout: 30000}
  );
  if (!shouldApply()) {
    return { results: [], hasMore: false, page }
  }

  if (res.status === 200) {
    const data = normalizeAccountsResponse(res.data, page)
    const results = filterAccountsLocally(data.results, filterBy, filter)
    setState({
      accounts: mergeAccountsById(currentAccounts, results, reset),
      accountsPage: data.page,
      accountsHasMore: data.hasMore,
      accountsLoadingMore: false,
      loading: false,
    })
    return data
  }
  setState({ [loadingKey]: false })
  return { results: [], hasMore: false, page }
}

const getLiveAccount = async (enterprise_id, account, setState) => {
  if (!account?.id) {
    return account
  }

  setState({ loading: true })
  try {
    const res = await api.get(
      `api/enterprises/${enterprise_id}/scout_accounts/${account.id}/?live=1`,
      { timeout: 30000 }
    )

    if (res.status === 200) {
      return { ...account, ...res.data }
    }
  } catch (error) {
    console.warn('Falha ao atualizar dados do número Scout', error)
  } finally {
    setState({ loading: false })
  }

  return account
}

const MonitorPage = () => {
  const { configurableUrls } = useMenu()

  const windowRef = {};

  const enterprise = getSession().profile.enterprise;
  const scout_ai = enterprise.platforms.scout_ai;
  const location = useLocation();
  const history = useHistory();

  const [state, setState] = useReducer(lockedReducer, {
    chats: [],
    accounts: [],
    accountsPage: 1,
    accountsHasMore: false,
    accountsLoadingMore: false,
    activeAccount: null,
    activeChat: null,
    chatMessageId: null,
    loading: false,
    socket: null,
    showSearch: false,
    showCRMSidebar: false,
    showAnalyticsSidebar: false,
    analyticsDate: (() => {
      const d = new Date();
      d.setDate(d.getDate());
      return d.toISOString().slice(0, 10);
    })(),
    //History dialog state
    showChatHistory: false,
    historyChat: null,
    historyMessages: [],
    uniqueId: null,
    lastDateTime: null,
    platform: null,
    uniqueId: null,
    nick: null,
    accountcode: null,
    dashboardDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    })(),
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [accountFilterBy, setAccountFilterBy] = useState('name');
  const [appliedAccountFilterBy, setAppliedAccountFilterBy] = useState('name');
  const [accountFilterText, setAccountFilterText] = useState('');
  const [debouncedAccountFilterText, setDebouncedAccountFilterText] = useState('');
  const accountsRequestRef = useRef(0);
  const accountFilterTextRef = useRef(accountFilterText);

  useEffect(() => {
    accountFilterTextRef.current = accountFilterText;

    const debounceTimer = window.setTimeout(() => {
      setDebouncedAccountFilterText(accountFilterText.trim())
      setAppliedAccountFilterBy(accountFilterBy)
    }, 350)

    return () => window.clearTimeout(debounceTimer)
  }, [accountFilterText]);

  const effectiveAccountFilterBy = debouncedAccountFilterText ? appliedAccountFilterBy : 'name';

  useEffect(() => {
    const queryFilter = debouncedAccountFilterText;
    const queryFilterBy = effectiveAccountFilterBy;
    const requestId = accountsRequestRef.current + 1
    accountsRequestRef.current = requestId
    setState({
      accountsPage: 1,
      accountsHasMore: false,
      accountsLoadingMore: false,
      loading: true,
    })

    getAccounts(enterprise.id, setState, {
      page: 1,
      filterBy: queryFilterBy,
      filter: queryFilter,
      reset: true,
      loadingKey: 'loading',
      currentAccounts: [],
      shouldApply: () => (
        accountsRequestRef.current === requestId
        && accountFilterTextRef.current.trim() === queryFilter
      ),
    }).catch(() => {
      if (accountsRequestRef.current === requestId) {
        setState({ loading: false })
      }
    });
  }, [debouncedAccountFilterText, effectiveAccountFilterBy, enterprise.id]);

  const handleAccountSelect = async (account) => {
    const liveAccount = await getLiveAccount(enterprise.id, account, setState)
    setState({ activeAccount: liveAccount, activeChat: null })
  }

  const handleLoadMoreAccounts = () => {
    if (state.loading || state.accountsLoadingMore || !state.accountsHasMore) {
      return
    }

    const requestId = accountsRequestRef.current
    getAccounts(enterprise.id, setState, {
      page: state.accountsPage + 1,
      filterBy: effectiveAccountFilterBy,
      filter: debouncedAccountFilterText,
      reset: false,
      loadingKey: 'accountsLoadingMore',
      currentAccounts: state.accounts,
      shouldApply: () => accountsRequestRef.current === requestId,
    }).catch(() => setState({ accountsLoadingMore: false }))
  }

  useEffect(async () => {
    const queryParams = new URLSearchParams(location.search);
    const account = queryParams.get("account");
    const chat = queryParams.get("chat");

    if (account && chat) {
      queryParams.set("live", "1");
      const res = await api.get(
        `api/enterprises/${
          enterprise.id
        }/scout_accounts/?${queryParams.toString()}`
      );

      if (res.status === 200 && res.data.length > 0) {
        const account = res.data[0];
        const resc = await api.get(
          `api/enterprises/${enterprise.id}/scout_accounts/${account.id}/conversation/${chat}/`
        );
        if (resc.status === 200 && resc.data.length > 0) {
          const activeChat = { account_id: account.id, ...resc.data[0] };
          setState({
            activeAccount: account,
            activeChat: activeChat,
            chatMessageId: null,
            showSearch: false,
            showCRMSidebar: true,
            showAnalyticsSidebar: true,
          });
        }
      }
      const newParams = new URLSearchParams();
      history.replace({ search: newParams.toString() }); // Update the URL without the query parameter
    }
  }, [location]);

  const handleCloseHistoryMessages = () => {
    let state = {
      showChatHistory: false,
    };
    setState(state);
  };

  window.setState = setState;

  return (
    <>
      {state.showChatHistory && (
        <HistoryMessagesDialog
          activeChat={state.historyChat}
          open={state.showChatHistory}
          uniqueId={state.uniqueId}
          nick={state.nick}
          lastDateTime={state.lastDateTime}
          historyMessages={state.historyMessages}
          platform={state.platform}
          accountcode={state.accountcode}
          handleClose={() => handleCloseHistoryMessages()}
        />
      )}
      <Loading loading={state.loading} />
      <ScoutSocketComponent
        parent={state}
        setParent={setState}
        windowRef={windowRef}
      />
      {enterprise.platforms.scout ? (
        <>
          <Box className="scout-wrapper">
            <Box className="scout-searchbar">
              { scout_ai && <>
                <Button
                  onClick={() => {
                    setState({
                      activeAccount: null,
                      activeChat: null,
                      chatMessageId: null,
                      showSearch: false,
                      showCRMSidebar: false,
                    });
                  }}
                  disabled={state.activeAccount === null}
                >
                  {" "}
                  Visão geral{" "}
                </Button>
                <Box>
                  <TextField
                    id="dateEndTime"
                    size="small"
                    label="Data"
                    type="date"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={state.dashboardDate || ""}
                    onChange={(e) => setState({ dashboardDate: e.target.value })}
                  />
                </Box>
              </>}
              <Paper
                component="form"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: 500,
                  height: 40,
                  bgcolor: "#efefef",
                  border: "1px solid #bebec0",
                  boxShadow: "unset"
                }}
              >
                <Tooltip
                  title="Procure nas conversas."
                  placement="bottom"
                  disableInteractive
                >
                  <InputBase
                    sx={{ ml: 2, flex: 1 }}
                    placeholder="Busca"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setState({ showSearch: true });
                      }
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    }
                  />
                </Tooltip>
              </Paper>
            </Box>
            <Box className="scout-accounts">
              <Box className={state.loading && state.accounts.length ? "scout-accountlist scout-accountlist--refreshing" : "scout-accountlist"}>
                {!state.activeAccount ? (
                  <Box className="scout-account-filter-bar">
                    <TextField
                      select
                      size="small"
                      className="scout-account-filter-field scout-account-filter-field--select"
                      variant="standard"
                      value={accountFilterBy}
                      InputProps={{ disableUnderline: true }}
                      onChange={({ target }) => setAccountFilterBy(target.value)}
                    >
                      {scoutAccountFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      className="scout-account-filter-field scout-account-filter-field--search"
                      variant="standard"
                      placeholder={accountFilterBy === 'phone_number' ? 'Buscar por número' : 'Buscar por nome'}
                      value={accountFilterText}
                      InputProps={{ disableUnderline: true }}
                      onChange={({ target }) => setAccountFilterText(target.value)}
                    />
                  </Box>
                ) : null}
                <AccountList
                  parent={state}
                  setParent={setState}
                  onAccountSelect={handleAccountSelect}
                  onLoadMore={handleLoadMoreAccounts}
                />
              </Box>
              <Box className="scout-chatwindow">
                <ChatWindow
                  parent={state}
                  setParent={setState}
                  innerRef={windowRef}
                />
              </Box>
              {state.showCRMSidebar && (
                <Box className="scout-rightsidebar">
                  <CRMSideBar parent={state} setParent={setState} />
                </Box>
              )}
              {state.showAnalyticsSidebar && scout_ai && state.activeAccount && state.activeChat && (
                <Box className="scout-rightsidebar">
                  <AnalyticsSideBar
                    analyticsDate={state.analyticsDate}
                    accountId={state.activeAccount.id}
                    conversationId={state.activeChat.id}
                    conversationType={state.activeChat.type}
                    setParent={setState}
                  />
                </Box>
              )}
            </Box>
          </Box>
          {state.showSearch && (
            <TextSearch
              searchQuery={searchQuery}
              handleClose={() => setState({ showSearch: false })}
              parent={state}
              setParent={setState}
            />
          )}
        </>
      ) : (
        <ContentUnavailable 
          icon={<img src={ScoutLogo} alt="Kwik Scout" style={{ width: 350 }} />}
          title="Recurso indisponível para sua conta."
          description={
            <a href={configurableUrls?.scout ? configurableUrls.scout : "https://scout.kwik.app.br/"} target="_blank" rel="noopener noreferrer">
                  Para mais informações sobre o Kwik Scout, clique aqui.
              </a>
          }
        />
      )}
    </>
  );
};

export default MonitorPage;
